import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { App } from "antd5";
import { format } from "date-fns";
import { Flex, Text } from "styles/utility-components";

import { Currency, FormattedDate } from "components/app_layout/Typography";
import CompanyUserSelect from "components/form_components/CompanyUserSelect";
import { getFormDirtyValues } from "components/form_components/formUtils";
import { DatePicker, Input, TextArea } from "components/form_components/Inputs";
import Tag from "components/opportunities/saved/Tag";
import DisplayDate from "components/ui/date/DisplayDate";
import EditableText from "components/ui/editable_text/EditableText";
import Tooltip from "components/ui/tooltip/Tooltip";
import { createUseDebounce } from "lib/debounce";
import { OpportunityUpdateInput } from "lib/generated/app-service-gql/graphql";
import { OpportunityDetails } from "lib/hooks/api/opportunities/useOpportunity";
import { useUpdateOpportunity } from "lib/hooks/api/opportunities/useUpdateOpportunity";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import UIcon from "../../lib/icons/untitled_ui/UIcon";
import { generateContactsUrl } from "../contacts/utils";
import { BuyerActivity } from "./buyer_activity/BuyerActivity";
import { OpportunityStageDropdownSelect } from "./opportunity_stage/OpportunityStageDropdownSelect";
import EntitySelector from "./saved/EntitySelector";
import { OpportunityContacts } from "./saved/OpportunityContacts";
import { AutoQualifyCard } from "./AutoQualifyCard";
import { filterOpportunityDefaultValues } from "./utils";

type OpportunityContentProps = {
  opportunity: OpportunityDetails;
};

type EditableFields = keyof Pick<
  OpportunityUpdateInput,
  | "value"
  | "routeToMarket"
  | "clarificationDeadline"
  | "submissionDate"
  | "startDate"
  | "existingExpiryDate"
  | "closeDate"
>;

const dateFields: {
  label: string;
  name: keyof Pick<
    OpportunityDetails,
    "clarificationDeadline" | "submissionDate" | "startDate" | "existingExpiryDate" | "closeDate"
  >;
  tooltip?: string;
}[] = [
  {
    label: "Clarification deadline",
    name: "clarificationDeadline",
  },
  {
    label: "Submission date",
    name: "submissionDate",
  },
  {
    label: "Start date",
    name: "startDate",
  },
  {
    label: "Existing expiry date",
    name: "existingExpiryDate",
  },
  {
    label: "Est. close date",
    name: "closeDate",
    tooltip:
      "Est. closed date can be used to show when you expect this opportunity to actually close. We auto-populate this with either the expiry date or submission date depending on the procurement stage",
  },
];

export default function OpportunityContent({ opportunity }: OpportunityContentProps) {
  const { message } = App.useApp();
  const useDebounce = createUseDebounce(500);
  const [editingField, setEditingField] = useState<EditableFields | undefined>();

  const { openPaywallModal } = usePaywall();

  const subscription = useSubscription();
  const isFreeOrBasic = !subscription.hasDataTypes("BUYERS|SUPPLIERS");

  const {
    control,
    handleSubmit,
    watch,
    formState: { dirtyFields },
    reset,
  } = useForm<OpportunityUpdateInput>({
    defaultValues: filterOpportunityDefaultValues(opportunity),
  });

  const { mutate: updateOpportunity } = useUpdateOpportunity(
    {
      onSuccess: () => {
        message.success("Opportunity updated successfully");
        setEditingField(undefined);
        // we just want to reset the form dirty field state
        reset({}, { keepValues: true });
      },
      onError: (error) => {
        console.warn("Failed to update opportunity", error);
        message.error("Failed to update opportunity");
      },
    },
    {
      startStageId: opportunity.stage.id,
      buyerNames: opportunity.buyers.map(({ name }) => name),
      name: opportunity.name,
    },
  );

  const onSubmit: SubmitHandler<OpportunityUpdateInput> = (values: OpportunityUpdateInput) => {
    const updatedValues = getFormDirtyValues(dirtyFields, values);
    const filteredPayload = {
      ...updatedValues,
      id: values.id,
    };

    if (updatedValues.value !== undefined) {
      filteredPayload.value =
        String(updatedValues.value) === "" ? null : Number(updatedValues.value);
    }

    // Handle assignedToId explicitly
    if (dirtyFields.assignedToId) {
      filteredPayload.assignedToId = updatedValues.assignedToId ?? null;
    }

    updateOpportunity({ opportunity: filteredPayload });
  };

  const debouncedSubmit = useDebounce((values: OpportunityUpdateInput) => {
    onSubmit(values);
  });

  useEffect(() => {
    const subscription = watch((_, { name, type }) => {
      if (type === "change" && name !== "value" && name !== "routeToMarket") {
        return handleSubmit(debouncedSubmit)();
      }
    });
    return () => subscription.unsubscribe();
  }, [debouncedSubmit, handleSubmit, watch]);

  /**
   * This paywall should be displayed when a freemium user stays on the page for 30 seconds
   */
  useEffect(() => {
    if (!isFreeOrBasic) return;

    // Do not show paywall if the user has BIDS data type
    if (subscription.hasDataTypes("BIDS") && opportunity.stage.name === "Bid / no-bid") return;

    const timer = setTimeout(() => {
      openPaywallModal("OPPORTUNITY", { strategyPaywall: true });
    }, 30000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastUpdatedDate = opportunity?.updatedAt;

  return (
    <Content>
      <Flex flexDirection="column" gap={4} width="100%">
        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, value } }) => (
            <EditableText
              textProps={{ h1: true, bold: true, children: value }}
              placeholder="Enter opportunity title"
              emptyText="Untitled"
              onSave={onChange}
              padding="8px 0"
              autoFocus
            />
          )}
        />

        {lastUpdatedDate && (
          <Tag
            title={
              <DisplayDate
                label="Last updated"
                formatFn={() => format(new Date(lastUpdatedDate), "do MMMM yyyy")}
              />
            }
            icon="clock"
          />
        )}
      </Flex>
      <Flex column gap={48}>
        <form>
          <Section>
            <ClassNames>
              {({ css }) => (
                <Grid>
                  <GridItem>
                    <FormItemKey>Stage</FormItemKey>
                  </GridItem>
                  <GridItem>
                    <OpportunityStageDropdownSelect
                      name="stageId"
                      control={control}
                      label=""
                      variant="borderless"
                      className={css({ maxWidth: 250 })}
                    />
                  </GridItem>
                  <GridItem>
                    <FormItemKey>Assignee</FormItemKey>
                  </GridItem>
                  <GridItem>
                    <CompanyUserSelect
                      name="assignedToId"
                      label=""
                      control={control}
                      variant="borderless"
                      className={css({ maxWidth: 250 })}
                    />
                  </GridItem>
                </Grid>
              )}
            </ClassNames>
          </Section>

          {opportunity.stage.transitionGroup != "Pre_Engagement" && (
            <ClassNames>
              {({ css }) => (
                <Section className={css({ maxWidth: 720 })}>
                  <AutoQualifyCard opportunityId={opportunity.id} bidId={opportunity.bid?.id} />
                </Section>
              )}
            </ClassNames>
          )}

          <Section>
            <SectionHeader>
              <IconContainer>
                <UIcon icon="infoCircle" size={16} />
              </IconContainer>
              <Text h2>Overview</Text>
            </SectionHeader>
            <Grid>
              <GridItem>
                <FormItemKey>Buyer</FormItemKey>
              </GridItem>
              <GridItem>
                <EntitySelector
                  opportunityId={opportunity.id}
                  entityType="buyer"
                  url={routes.buyers}
                  entities={opportunity.buyers?.map(({ id, name }) => ({ id, name }))}
                />
              </GridItem>

              <GridItem>
                <FormItemKey>Value</FormItemKey>
              </GridItem>
              <GridItem>
                {editingField === "value" ? (
                  <Input
                    name="value"
                    label=""
                    control={control}
                    type="number"
                    placeholder="Value of the opportunity"
                    autoFocus
                    onBlur={async () => {
                      setEditingField(undefined);
                      await handleSubmit(onSubmit)();
                    }}
                    // Prevent Enter key from submitting the form
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                  />
                ) : (
                  <FormItemValue onClick={() => setEditingField("value")}>
                    <Currency value={opportunity.value} longFormat />
                  </FormItemValue>
                )}
              </GridItem>

              <GridItem>
                <FormItemKey>Incumbent supplier</FormItemKey>
              </GridItem>
              <GridItem>
                <EntitySelector
                  opportunityId={opportunity.id}
                  entityType="supplier"
                  url={routes.suppliers}
                  entities={opportunity.suppliers?.map(({ id, name }) => ({ id, name }))}
                />
              </GridItem>

              <GridItem>
                <FormItemKey>Route to market</FormItemKey>
              </GridItem>
              <GridItem>
                {editingField === "routeToMarket" ? (
                  <TextArea
                    autoSize
                    name="routeToMarket"
                    label=""
                    control={control}
                    aria-label="Route to market"
                    onPressEnter={(e) => {
                      if (e.shiftKey) return;

                      e.preventDefault();
                      e.stopPropagation();
                      return handleSubmit(onSubmit)();
                    }}
                    onBlur={async () => {
                      setEditingField(undefined);
                      await handleSubmit(onSubmit)();
                    }}
                  />
                ) : (
                  <FormItemValue
                    onClick={() => setEditingField("routeToMarket")}
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    <Text>{opportunity.routeToMarket || "Click to add"}</Text>
                  </FormItemValue>
                )}
              </GridItem>
            </Grid>
          </Section>

          <Section>
            <SectionHeader>
              <IconContainer>
                <UIcon icon="calendar" size={16} />
              </IconContainer>
              <Text h2>Key dates</Text>
            </SectionHeader>

            <Grid>
              {dateFields.map(({ name, label, tooltip }) => (
                <React.Fragment key={name}>
                  <GridItem>
                    <FormItemKey>
                      {label}
                      {tooltip && (
                        <Tooltip content={tooltip} variant="dark">
                          <span>
                            <UIcon icon="infoCircle" size={12} />
                          </span>
                        </Tooltip>
                      )}
                    </FormItemKey>
                  </GridItem>
                  <GridItem>
                    {editingField === name ? (
                      <DatePicker name={name} label="" control={control} />
                    ) : (
                      <FormItemValue onClick={() => setEditingField(name)}>
                        <FormattedDate date={opportunity[name]} placeholder="Select a date" />
                      </FormItemValue>
                    )}
                  </GridItem>
                </React.Fragment>
              ))}
            </Grid>
          </Section>
        </form>
        <BuyerActivity opportunity={opportunity} />
        <Flex column gap={24} aria-label="Outreach">
          <Flex alignItems="center" gap={8}>
            <IconContainer>
              <UIcon icon="announcement02" size={16} />
            </IconContainer>
            <Text h2>Outreach</Text>
          </Flex>
          <OpportunityContacts
            contacts={opportunity?.contacts ?? []}
            entityUrl={generateContactsUrl({
              buyerGuids: opportunity.buyers?.map((buyer) => buyer.id) ?? [],
            })}
            opportunityId={opportunity.id}
            showAddEntity={false}
          />
        </Flex>
      </Flex>
    </Content>
  );
}

const Content = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  width: "100%",
  padding: "48px 96px",
  overflow: "auto",
  // To prevent the content from being cut off
  marginBottom: 80,
});

const Section = styled(Flex)({
  flexDirection: "column",
  gap: 24,
  width: "100%",
  marginTop: 48,
});

const SectionHeader = styled(Flex)({
  flexDirection: "row",
  gap: 8,
  alignItems: "center",
});

const IconContainer = styled.div(({ theme }) => ({
  padding: 8,
  backgroundColor: theme.colors.sysBackgroundAlternative,
  height: 24,
  width: 24,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 4,
}));

const Grid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 3fr",
  rowGap: 16,
  columnGap: 16,
  width: 600,
});

const GridItem = styled.div({
  alignContent: "start",
  minHeight: 32,
});

const FormItemKey = styled(Flex)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  padding: "4px 0",
  alignItems: "center",
  gap: 4,
}));

const FormItemValue = styled(Flex)(({ theme }) => ({
  cursor: "pointer",
  alignItems: "center",
  minHeight: 32,
  // To match the padding of other components
  padding: "0 11px",
  borderRadius: 4,
  transition: "background 0.2s ease",

  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },
}));
